<!--

Copyright Paul B. Lucas (C) 2025   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when invoked by a user on their own data.   System administration functions 
are not allowed nor are supervisor properties.  These require department Admin privileges and are only available
from the ViewEditEmployeeEntire page with appropriate permissions.    This is a simplification of the
ViewEditEmployeeEntire view to allow non-claim properties of the used to be <self-edited class="
Claim properties in Entra are read-only:  These include: userPrincipleName (email), name, (netid), UIN, 
job title, and other claims.

Properties which are not claims can be edited: campus mail code, home addres, and contact info.

Since these changes are to the user which is a store state object and cannot be directly mutated, 
edits to the page are performed on a non-referential copy called editedUser.  This is saved to the database 
using the SAVE command.  The user is then refetched from the database and the store state user updated, 
and a new editedUser generated on the store state.


Revisions:
    02/25/2025      pblucas     initial creation 
    

-->
<template>
    <div class="_master_container">
        <div class="_flag_container" v-if="saveFlag">
          <p class="_p_flags">Saved User Data</p>
        </div>
        <!-- first row - full name of user being edited - computed proxy -->
        <div class="_container_r1" >
            <div class="_fieldset" style="order: 1;">
                <div v-if="!config['suppress-photos']  && !editedUser['req-photo-suppress']">
                    <img class="_user_image" width="300" :src="editedUser.image">
                </div>
                <div v-else>
                    <img class="_user_image" src="@/assets/defaultPhoto.png" >
                </div>
                <div>
                    <p class="_p_annotation_3">UUID: <span class="_p_diminished"> {{ editedUser.uuid }}</span></p>
                </div>
            </div>
            <div class="_fieldset" style="order: 2;">
                <p class="_p_user_name">{{ editedUser['name-concat'] }}</p>
            </div>
        </div>
        <!-- second row is two fieldset columns: PID and home address -->
        <div class="_container_r2" >
            <div id="prop_set_1" class="_fieldset" style="order: 1;">
                <p class="_p_fieldset_name">Personal Identifying Information</p>
                <p class="_p_annotation">This information, with exception of campus mailcode, is attached to your identity,
                    and is returned on authentication.  These properties cannot be edited.  
                </p>
                <div class="_fieldset_box">
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop1">First Name</label>
                        </div>
                        <div class="_col_60">
                            <!-- extractable from 'name' in claims -->
                            <p id="set1_prop1" class="_p_non_editable_value">{{ editedUser['first-name'] }}</p> 
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop2">Last Name</label>
                        </div>
                        <div class="_col_60">
                            <!-- extractable from 'name' in claims -->
                            <p id="set1_prop2" class="_p_non_editable_value">{{ editedUser['last-name'] }}</p> 
                       </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop3">Middle Initial</label>
                        </div>
                        <div class="_col_60">
                            <!-- extractable from 'name' in claims -->                           
                            <p id="set1_prop3" class="_p_non_editable_value">{{ editedUser['middle-initial'] }}</p> 
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop4">Netid</label>
                        </div>
                        <div class="_col_60">
                            <!-- netid extractable from userPrincipleName in claims -->
                            <p id="set1_prop4" class="_p_non_editable_value">{{ editedUser['netid'] }}</p> 
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop5">UIN</label>
                        </div>
                        <div class="_col_60">
                            <!-- UIN present in optional claims -->
                            <p id="set1_prop5" class="_p_non_editable_value">{{ editedUser['uin'] }}</p> 
                         </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop6">Job Title</label>
                        </div>
                        <div class="_col_60">
                            <!-- job title present in optional claims-->
                            <p id="set1_prop6" class="_p_non_editable_value">{{ editedUser['title'] }}</p> 
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop7">E Class Code</label>
                        </div>
                        <div class="_col_60">
                            <!-- eclass code present Banner - not in claims -->
                            <p id="set1_prop6" class="_p_non_editable_value">{{ editedUser['eclass-code'] }}</p> 
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop8">Home Org</label>
                        </div>
                        <div class="_col_60">
                            <!-- home org present in Banner - not in claims-->
                            <p id="set1_prop6" class="_p_non_editable_value">{{ editedUser['home-org'] }}</p> 
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop9">Campus Mail Code</label>
                        </div>
                        <div class="_col_60">
                            <!-- editable -->
                            <input id="set1_prop9" class="_input" v-model="editedUser['mailcode']" />
                        </div>
                    </div>
                 </div>
            </div>
            <div id="prop_set_2" class="_fieldset" style="order: 2;">
                <p class="_p_fieldset_name">Home Address</p>
                <p class="_p_annotation">Home address is pre-populated with your Banner home address if available.
                    <br /><br />
                </p>

                <div class="_fieldset_box">
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop1">Address 1</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop1" class="_input" v-model="editedUser['address1']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop2">Address 2</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop2" class="_input" v-model="editedUser['address2']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop3">City</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop3" class="_input" v-model="editedUser['city']" />
                        </div>
                    </div>
                    <div v-if="editedUser['country'] != 'United States' && editedUser['country'] != 'USA'">
                        <div class="_row">
                            <div class="_col_40">
                                <label class="_input_label" for="set2_prop4">State</label>
                            </div>
                            <div class="_col_60">
                                <input id="set2_prop4" class="_input" v-model="editedUser['state']" />
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div class="_row">
                            <div class="_col_40">
                                <label class="_input_label" for="set2_prop4">State</label>
                            </div>
                            <div class="_col_60">
                                <select id="set2_prop4" class="_select" v-model="editedUser['state']">
                                    <option id="set1a_prop2" class="_type_list" v-for="(item, idx) in aStates" 
                                        aria-label="State/Territory selection options" 
                                        aria-description="Dropdown l ist to select state or gterritory if in USA"
                                        :value="item"> {{item}} 
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop5">Postcode (Zipcode)</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop5" class="_input" v-model="editedUser['postcode']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop6">Country</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop6" class="_input" v-model="editedUser['country']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop7">Timezone</label>
                        </div>
                        <div class="_col_60">
                            <select v-model="editedUser['timezone']" class="_select">
                                <option id="set2_prop7" class="_type_list" disabled> {{editedUser['timezone']}} </option>
                                <option id="set2_prop7" class="_type_list" v-for="(zone, idx) in aTimezones" :value="zone.zone" > {{zone.zone}} </option>
                            </select>
                        </div>
                    </div>
                    <br />
                    <p class="_p_annotation">Miscellaneous</p>
                    <div class="_row">
                        <div class="_col_8">
                            <input id="set2_prop8" class="_check" type="checkbox" v-model="editedUser['req-photo-suppress']" value=true>
                        </div>
                        <div class="_col_90">
                            <label class="_check_label" for="set2_prop8">Employee requests photo to be suppressed</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- third row is two fieldset columns, user contacts, and supervisor -->
        <div class="_container_r3" >
            <div id="prop_set_3" class="_fieldset" style="order: 1;">
                <p class="_p_fieldset_name">Contacts</p>
                <div class="_fieldset_box" style="min-height: 90px;">
                    <!--  display any contacts already loaded -->          
                    <div class="_contacts_box"  v-if="editedUser.contacts.length > 0">
                        <div class="_row" v-for="(_,idx)  in editedUser.contacts">
                            <div class="_col_40" style=" overflow-x: hidden;">
                                <p class="_p_contact">{{ editedUser.contacts[idx].type }} </p>
                            </div>
                            <div class="_col_40" style="overflow-x: hidden;">
                                <p class="_p_contact"> {{ editedUser.contacts[idx].value}} </p>
                            </div>
                            <div class="_col_20" >
                                    <button class="_contact_button_delete" @click="deleteContact(idx)">Delete</button>                               
                            </div>
                        </div>
                    </div>  
                    <br />
                    <div class="_contacts_add_request" v-if="!bViewContactEntryForm" >
                        <button class="_contact_button_add" @click="toggleContactEntryForm">Add A Contact</button>
                    </div> 
                    <form  class='_form' v-if="bViewContactEntryForm">
                       
                        <div class="_row">
                            <div class="_scol_35">
                                <label class="_input_label" for="set3_prop1">Type</label>
                            </div>
                            <div class="_scol_50">
                                <select id="set3_prop1" class="_select" v-model="aContact.type" >
                                    <option class="_type_list" v-for="(item, idx) in aContactTypes" :value="item" > {{item}} </option>
                                </select>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_scol_35">
                                <label class="_input_label" for="set3_prop2">Target</label>
                            </div>
                            <div class="_scol_50">
                                <input id="set3_prop2" class="_input" type="text" v-model="aContact.value" @keydown.enter="addContact(user,aContact)">
                            </div>
                        </div>
                        <br />
                        <div class="_control_left_align" >
                             <button class="_contact_button_add" @click="addContact(aContact)">Add This Contact</button>
                             <button class="_contact_button_cancel" @click="cancelAddContact">Cancel</button>
                        </div>
                    </form>
                </div> 
            </div>            
        </div>
        <!-- forth row - affiliation and elligibility -->
        <!-- readonly properties - to be set only by administrator -->
        <div class="_container_r4" >
            <div id="prop_set_5" class="_fieldset" style="order: 1;">
                <p class="_p_fieldset_name">Affiliation</p>
                <p class="_p_annotation">Affiliation is determined through your employment contract and can only be edited 
                    by your unit HR representative.
                </p>
                <div class="_fieldset_box">
                   <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop1">College Name</label>
                        </div>
                        <div class="_col_60">
                            <p id="set5_prop1" class="_p_non_editable_value"> {{editedUser['college']}} </p>
                        </div>
                    </div>    
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop2">College Code</label>
                        </div>
                        <div class="_col_20">
                            <p id="set5_prop2" class="_p_non_editable_value"> {{editedUser['college-code']}} </p>
                        </div>
                   </div>
                   <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop3">Department Name</label>
                        </div>
                        <div class="_col_60">                           
                            <p id="set5_prop3" class="_p_non_editable_value"> {{editedUser['department']}} </p>                            
                        </div>
                   </div>
                   <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop4">Department Code</label>
                        </div>                    
                        <div class="_col_20">
                            <p id="set5_prop4" class="_p_non_editable_value"> {{editedUser['department-code']}} </p> 
                        </div>
                   </div>
                   <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop5">Division Name</label>
                        </div>
                        <div class="_col_60">                           
                            <p id="set5_prop5" class="_p_non_editable_value"> {{editedUser['division']}} </p>                            
                        </div>     
                   </div>
                   <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop6">Orgcode</label>
                        </div>
                        <div class="_col_20">                           
                            <p id="set5_prop6" class="_p_non_editable_value"> {{editedUser['division-code']}} </p>                            
                        </div>     
                   </div>
                </div>
            </div>
        </div>
        <div class="_employees_controls">
            <br/>
            <div class="_employees_inline_controls" >
                <button class="_main_button"  type="button" @click="saveUser(true)">Save changes</button>
                <button class="_main_button"  type="button" @click="cancelEdits()">Cancel </button>
            </div>
        </div>
        <!-- clearance for copyright -->
        <br /><br />

    </div>
</template>

<script>
    import { ref, computed }                from 'vue'
    import { useStore }                     from 'vuex'

    import useEmployeesAPI                  from '@/composables/useEmployeesAPI'
    import useGraphAPI                      from '@/composables/useGraphAPI'
    import useRefreshAPI                    from '@/composables/useRefreshAPI'
    import usePermissionsAPI                from '@/composables/usePermissionsAPI'


    // import enumerations
    import SysConfigs                       from '@/configuration/form-options.json'
    
    export default {
        components: {
        },
        props: [
            'claims',
            'gat'
        ],
        emits: [
        ],
    
        setup(props)    {
        
            const store  = useStore()
            const { checkStoreForRefresh }  = useRefreshAPI()
            const { checkAdminGroups }      = usePermissionsAPI()

            // there are refs and must be unwrapped with .value
            // user obtained from store first
            const user                  = computed( () => store.state.user)
            const config                = computed( () => store.state.config)   

            // all rendering and modification occur on this object
            // editedUser deeply duplicated from store second
            const editedUser            =   ref({})
            editedUser.value            =   JSON.parse(JSON.stringify(user.value))
                
            // handle enumerations from configuration data
            const sysConfigs          = ref(SysConfigs)
            
            // refer to contacts.class.inc.php
            const aContactTypes         =   ref(sysConfigs.value["contact-types"])        
            const aContact   = ref({
                'type':                     '',      // see type enums from sysConfigs
                'handle':                   '',
                'value':                    '',
                'reference':                ''
            })

            // other list enumerations
            const aTimezones            =   ref(sysConfigs.value['timezones'])
          
            // MARK: Entry processing for user based on user object in store
            // User will have been fetched/generated from APP.vue setup()
            // Refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            checkAdminGroups(props.claims)
  
                
            // use the following in this. context
            return {user, editedUser, config, store, aContact,  aContactTypes, 
                    sysConfigs, aTimezones
                    }
            },
       
        data() {

            return {
                // temporary working variables for forms
                bViewContactEntryForm:  false,
                bGrapherror:            false,
                renderState:            "FORM",
                aStates:                [],                   
                saveFlag:               false,          
             }
        },

        methods:  {
            useFormat(date) {
                const day = date.getDate();
                const month = date.getMonth() + 1;
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
            // FUNCTION - toggles contact entry form
            toggleContactEntryForm() {
                this.bViewContactEntryForm = !this.bViewContactEntryForm
            },
            deleteContact(idx) {
                this.editedUser.contacts.splice(idx,1) 
                // force rerender
                this.bViewContactEntryForm = false
            },
            addContact(c) {
                // fire store action
                this.editedUser.contacts.push(c)
                // reset the add form 
                this.aContact = {
                    'type':                     '',     
                    'handle':                   '',
                    'value':                    '',
                    'reference':                ''
                }
                this.bViewContactEntryForm = false
            },
            cancelAddContact() {
                // reset the add form Boolean
                this.aContact = {
                    'type':                     '',     
                    'handle':                   '',
                    'value':                    '',
                    'reference':                ''
                }
                this.bViewContactEntryForm = false
            },
            // FUNCTION: Saves user in databse, then fetches user and updates store
            saveUser(setFlag) {
                const { emplResults, emplKeyData, fetchEmployeesOnQuery, storeEmployee } = useEmployeesAPI()  
                const setUser  =   (employee) => this.store.dispatch('setUser', employee)
                const { checkAdminGroups } = usePermissionsAPI()

                storeEmployee(this.editedUser)
                .then( async () => {
                    if(!emplKeyData.value.response.status.includes('success'))  { 
                            console.log("ViewEditMe:: saveUser: failure to update employee")
                    } 
                    else {
                        // await then refetch to set user
                        await fetchEmployeesOnQuery({"netid":this.editedUser.netid})
                        .then( () => {
                            if(emplResults.value.length != 0) {
                                // employee found in RWA database
                                // update store
                                setUser(emplResults.value[0]) 
                                checkAdminGroups(this.claims, this.store)
                                // reset for future ops
                                this.editedUser = JSON.parse(JSON.stringify(this.user))
                                if(setFlag) this.saveFlag = true   
                            } 
                        })
                    }   
                })    
            },
            cancelEdits() {
                this.editedUser    =   JSON.parse(JSON.stringify(user.value)) 
            }
        },

        // hooks
        beforeMount() {
            this.aStates            =   this.sysConfigs['states-territories']
            // guard code to ensure that the Microsoft object-id is captured and preserved
            // Required for users if they are to be manipulated on security groups 
            const {getOIDOnUserPrincipalName, userResults, graphError } = useGraphAPI()

            // check user ms-object-id.  If not set, attempt to get it from graph.
            if(this.user['ms-object-id'].length == 0)   {
                // use only if user['ms-object-id'] is not available

                // this only works if the user has a principal email address corresponding
                // to his principleName
                if( this.user.contacts.length == 0) return

                // MARK: Obtain object-id for this user from graph
                let emplPrincipalName = this.user.contacts.filter( (c) =>     {
                    return c.type == "EMAIL"
                })[0].value
                // make sure a work email has been set
                if(typeof(emplPrincipalName != 'undefined'))    {
                    getOIDOnUserPrincipalName(this.gat, emplPrincipalName)
                    .then( ()  =>   {
                        // set ms-object-id on store
                        //console.log("ViewEditMe:: setup: Graph response object id: "+JSON.stringify(userResults.value.id))
                        this.editedUser['ms-object-id'] = userResults.value.id
                        this.saveUser(false)    // do not set saved flag
                    })
                    .catch( () => {
                        this.bGraphError = graphError.value
                    })
                }
            }

          
        },
        mounted()   {         
        },        
        updated() {
          
        }
    }
</script>

<style scoped>

/*  generalized flex layout and structure */
    ._master_container   {
        width: 98vw;
        height: 90%;
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        margin: 0 auto;
        padding: 0;
        box-sizing: border-box;
    }
    ._container_r1 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._container_r2 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._container_r3 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._container_r4 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._container_r5 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
   
    ._user_image {
        /* user image or default photo size to colum flex specified size */
        display: block;
        width: 40%;
        margin: 0 auto 0;
        padding: 0px;
    }
    ._p_user_name {
        width: 80%;
        margin: 50px auto 0;
        padding: 0;
        font-size: 12pt;
        font-weight: bold;
        color: #13294B;
    }

    /*  basic flex structure - two sets per row */
    ._fieldset    {
        /* break width into two even chunks */
        width: 50%;
        margin: 0 25px 0;
        padding: 0;
    }
    ._p_fieldset_name  {
        /* names fieldset across top above any box */
        margin: 3px 0;
        padding: 2px;
        color: #13294B;
        font-size: 0.8em;
        font-weight: bold;
        text-align: left;
    }
    ._fieldset_box {
        width: 100%;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color:#BFC9D9;
        margin: 0;
        padding: 3px 10px;
    }
    ._fieldset_box_eligibility {
        width: 100%;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color:#f9da98;
        text-align: left;
        margin: 0;
        padding: 3px 10px;
    }
    ._fieldset_box_admin {
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color:rgb(243, 83, 117);
        margin: 0;
        padding: 3px 10px;
    }
     ._flag_container {
        width: 25%;
        padding: 5px;
        float: right;
    }
    ._p_flags {
        font-size: 14pt;
        font-weight: bold;
        color:#FF5F05;
    }
    /* general controls styling */
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._contact_button_delete {
        background-color: #FF5F05;
        color: white;
        font-size: 0.6em;
        font-weight: normal;
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        border-color: #13294B;
        margin: 1px;
        padding: 2px 10px;
    }
    ._contact_button_delete:hover {
        background-color: white;
        color: red;
    }
    ._contact_button_add {
        background-color: #006230;     
        color: white;
        font-size: 0.6em;
        font-weight: normal;
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        border-color: #13294B;
        margin: 1px;
        padding: 2px 10px;
    }
    ._contact_button_add:hover {
        background-color: #FF5F05;  
        color: white;   
    }
    ._contact_button_cancel {
        background-color: #13294B;     
        color: white;
        font-size: 0.6em;
        font-weight: normal;
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        border-color: #13294B;
        margin: 1px;
        padding: 2px 10px;
    }
    ._contact_button_cancel:hover {
        background-color: #FF5F05;     
        color: white;
    }
    ._employees_controls {
        margin: 0;
        padding: 0;
    } 
    ._employees_inline_controls {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 5px;
    }
    /*  styling for existing contact management */
    ._contacts_box {
        /* area for existing contacts */
        margin: 0;
        padding: 2px;
        background-color:#BFC9D9;
        font-size: 0.8em;
        font-weight: normal;
        color: #13294B;
    }
    /* styling for new contact form */
    ._contacts_add_request     {
        margin: 0 0 0 10px;
        padding: 0;
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
    }
    ._form     {
        margin: 0;
        padding: 0;
    }
    ._control_left_align {
        margin: 0px 0 0 10px;
        padding: 0;
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;     
    }
    ._contact {
        margin: 0;
        padding: 0;
    }
    ._p_non_editable_value {
        font-size: 0.8em;
        font-weight: normal;
        color: #006230;
        text-align: left;
        margin: 0 0 0 5px;
    }
    ._inline_icon {
        display: inline; 
        padding: 0px 25px 0px 0px;;
        /* padding: 0 2px 0 3px; */
        height: 16px;
    }
    ._inline_icon_left {
        display: inline;
        height: 10px;
        margin: 0 0 0 10px;
    }
    /*  eligibility criteria - comditioned on config    */
    ._p_annotation {
        font-size: 0.7em;
        color:#006230;
        text-align: left;
        margin: 2px 0 1px;
        padding: 0 2px;
    }
    ._p_annotation_3 {
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        margin: 2px 0 1px;
        padding: 0 10px 0 5px;
    }
    ._p_diminished {
        /* specifies the CSS props for the uuid information */
        text-align: left;
        font-size:  0.9em;
        font-weight: normal;
        color: #13294B;
        margin: 0;
        padding: 2px;
    }
    ._p_diminished_grn {
        text-align: left;
        font-size:  0.8em;
        color: #006230;
        margin: 0;
        padding: 2px;
    }
    ._p_item_title   {
        width: 100%; 
        font-size: 0.9em;
        font-weight: normal;
        color: #13294B;
        text-align: left;
        margin: 0;
        padding: 2px 5px;
    }
   
    /* text styling for responsive form */
    ._col_8 {
        float: left;
        width:8%;
        margin-top: 5px;
    }
    ._col_20 {
        float: left;
        width:20%;
        margin-top: 5px;
    }
    ._col_40 {
        float: left;
        text-align: left;
        width: 40%;
        margin-top: 5px;
    }
    ._col_50 {
        float: left;
        text-align: left;
        width: 50%;
        margin-top: 5px;
    }

    ._col_60 {
        float: left;
        width: 60%;
        margin-top: 5px;
    }
    ._col_90 {
        float: left;
        width: 90%;
        margin-top: 5px;
    }
    ._row {
        text-align: left;
    }
    ._row:after {
        content: "";
        display: table;
        clear: both;
    }
    ._input, ._select, ._type_list {
        max-width: 100%;
        margin: 0 10px 0 0;
        padding: 2px 0 0 15px;
        font-size: 0.8em;
        border-style: solid;
        border-width: 1px;
        border-color: black;
        background-color: white;
        color: black;
        box-sizing: border-box; 
        resize: vertical;
    }
    ._type_list:disabled {
        color: green;
        font-weight: bold;
    }
  
    ._input_label {
        display: inline-block;  
        color: blue;
        font-weight: bold;
        font-size: 0.8em;
        text-align: left;
        padding: 2px 0;  
    }
    ._input_label:after {
        content:":";
    }
    ._check_label {
        color: blue;
        font-weight: bold;
        font-size: 0.8em;
        text-align: left;
        margin: 2px 0;
        padding: 2px 0;
    }
    ._p_contact {
        display: inline;
        text-transform: none;
        font-size: 0.9em;
        font-weight: bold;
        color: blue;
        text-align: left;
        margin: 0;
        padding: 0 5px 0; 
    }
    ._p_contact_delete {
        display: inline;
        text-transform: none;
        font-size: 0.9em;
        font-weight: normal;
        color: red;
        text-align: left;
        margin: 0;
        padding: 0; 
    }
    ._inline_icon {
        display:inline;
        height: 10px;
        margin: 0;
        padding: 0 3px 0 0;
    }
    ._date_picker {
        --dp-font-size: 0.8em;
        --dp-input-padding: 6px 10px 6px 10px;
        --dp-text-color: blue;
    }
    ._scol_8 {
        float: left;
        width: 8%;
        margin-top: 5px;
    }
    ._scol_35 {
        float: left;
        width: 35%;
        margin-top: 5px;
    }
    ._scol_50 {
        float: left;
        width: 50%;
        margin-top: 5px;
    }
    ._scol_80 {
        float: left;
        width: 80%;
        margin-top: 5px;
    }
    ._checks {
        width: 5%;
        text-align: center;
        margin: 0 auto;
        padding: 0;
    }
    @media screen and (max-width: 620px) {
        ._col_20, ._col_40, ._col_50, ._col_60  {
            width: 100%;
            margin-top: 0;
        }
        ._fieldset_box_admin {
            width: 100%;
            margin-top: 0;
        }
    }
    @media screen and (max-width: 800px) {
        ._container_r1, 
        ._container_r2,
        ._container_r3,
        ._container_r4,
        ._container_r5 {
            flex-wrap: wrap;  
        }
    }
    
 
</style>